/**
 * Embedded inside Shopify.js's tab area. Receives storeId from parent.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ChevronLeft, ChevronRight, Users as UsersIcon } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v, code) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code || 'USD', maximumFractionDigits: 0,
    }).format(Number(v));
  } catch { return `${code || ''} ${Number(v).toLocaleString()}`; }
};
const dateFmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ShopifyCustomers = ({ storeId }) => {
  const { showError } = useToast();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vipTier, setVipTier] = useState('');
  const [sortBy, setSortBy] = useState('ltv');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(1); }, [storeId]);
  useEffect(() => { const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350); return () => clearTimeout(t); }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize, sortBy });
      if (storeId) params.set('storeId', storeId);
      if (search) params.set('search', search);
      if (vipTier) params.set('vipTier', vipTier);
      const res = await api.get(`/api/shopify/customers?${params}`);
      setRows(res.data.customers || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
    } catch (e) {
      showError('Failed to load customers: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, storeId, search, vipTier, sortBy, showError]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="shop-panel">
      <div className="shop-tab-toolbar">
        <select className="shop-select" value={vipTier} onChange={(e) => { setVipTier(e.target.value); setPage(1); }}>
          <option value="">All tiers</option>
          <option value="Gold">Gold</option>
          <option value="Silver">Silver</option>
          <option value="Bronze">Bronze</option>
        </select>
        <select className="shop-select" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
          <option value="ltv">Lifetime spend</option>
          <option value="orders">Order count</option>
          <option value="recent">Recently updated</option>
          <option value="newest">Newest signups</option>
        </select>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
          <input
            className="shop-select"
            style={{ paddingLeft: 32, width: '100%' }}
            placeholder="Search email, phone, name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="shop-loading" style={{ minHeight: 200 }}><div className="shop-spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="shop-empty">
          <UsersIcon size={32} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: '0.5rem' }}>No customers found.</div>
        </div>
      ) : (
        <>
          <table className="shop-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email / Phone</th>
                <th>VIP</th>
                <th>Orders</th>
                <th>Lifetime spent</th>
                <th>City</th>
                <th>Last update</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <motion.tr key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
                  <td><Link to={`/shopify/customers/${c.id}`} className="shop-order-link">{c.display_name || '—'}</Link></td>
                  <td>
                    <div style={{ fontSize: '0.78rem' }}>{c.email || '—'}</div>
                    {c.phone && <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>{c.phone}</div>}
                  </td>
                  <td>{c.vip_tier ? <span className="shop-badge" style={{ background: tierBg(c.vip_tier), color: tierColor(c.vip_tier) }}>{c.vip_tier}</span> : '—'}</td>
                  <td>{c.total_orders}</td>
                  <td style={{ fontWeight: 600, color: '#10b981' }}>{moneyFmt(c.total_spent_amount, c.total_spent_currency)}</td>
                  <td>{c.default_city || '—'}</td>
                  <td className="shop-cell-time">{dateFmt(c.shopify_updated_at)}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          <div className="shop-pagination">
            <div>
              Show <select className="shop-select" style={{ display: 'inline-block', padding: '0.25rem 0.5rem' }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select> per page
            </div>
            <div className="shop-pagination-controls">
              <button className="shop-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /> Prev</button>
              <span className="shop-page-info">Page {page} of {pagination.totalPages || 1}</span>
              <button className="shop-page-btn" disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage(p => p + 1)}>Next <ChevronRight size={14} /></button>
            </div>
            <div className="shop-page-total">{pagination.total.toLocaleString()} customers total</div>
          </div>
        </>
      )}
    </div>
  );
};

const tierColor = (t) => ({ Gold: '#fbbf24', Silver: '#e5e7eb', Bronze: '#fb923c' }[t] || '#fff');
const tierBg = (t) => ({
  Gold: 'rgba(251, 191, 36, 0.18)',
  Silver: 'rgba(229, 231, 235, 0.18)',
  Bronze: 'rgba(251, 146, 60, 0.18)',
}[t] || 'rgba(255,255,255,0.06)');

export default ShopifyCustomers;
